"use client";

import { useCallback, useState } from "react";
import { apiErrorMessage } from "../lib/apiError";

type MutationState<TArgs extends unknown[], TData> = {
  /** Runs the call. Resolves to the data on success, or null on failure. */
  mutate: (...args: TArgs) => Promise<TData | null>;
  loading: boolean;
  /** The server's `detail` when it sent one, else the fallback message. */
  error: string | null;
  clearError: () => void;
};

/**
 * Wraps a generated SDK write call with its in-flight and error state.
 *
 * Routes that declare their error responses return `{ detail: string }`, so a
 * 409 surfaces the server's own wording ("That user is already a recipient")
 * rather than a generic message.
 */
export function useMutation<TArgs extends unknown[], TData>(
  mutator: (...args: TArgs) => Promise<{ data?: TData; error?: unknown }>,
  errorMessage: string,
): MutationState<TArgs, TData> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (...args: TArgs): Promise<TData | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await mutator(...args);
        if (result.error !== undefined) {
          setError(apiErrorMessage(result.error, errorMessage));
          return null;
        }
        return result.data ?? null;
      } catch {
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    // The mutator is typically an inline arrow; keying on it would defeat the
    // memo. Callers pass their dependencies through the call arguments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errorMessage],
  );

  const clearError = useCallback(() => setError(null), []);

  return { mutate, loading, error, clearError };
}
